'use client';
import React, { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import TopNav from '../components/TopNav';
import MarketTicker from '../components/MarketTicker';
import BreakingNewsBar from '../components/BreakingNewsBar';
import FooterBar from '../components/FooterBar';
import AppImage from '@/components/ui/AppImage';
import { ArrowRight, Clock, User, Filter, X, Calendar } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

const CATEGORY_COLORS: Record<string, string> = {
  BREAKING_NEWS: 'bg-primary',
  LATEST_NEWS: 'bg-blue-500',
  DEFI: 'bg-blue-600',
  REGULATION: 'bg-crypto-amber',
  MARKETS: 'bg-crypto-green',
  WEB3: 'bg-purple-600',
};

const CATEGORY_LABELS: Record<string, string> = {
  BREAKING_NEWS: 'Breaking News',
  LATEST_NEWS: 'Latest News',
  DEFI: 'DeFi',
  REGULATION: 'Regulation',
  MARKETS: 'Markets',
  WEB3: 'Web3',
};

const FILTER_CATEGORIES = ['Breaking News', 'Markets', 'DeFi', 'Regulation', 'Web3'] as const;

interface BlogPost {
  id: string;
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  thumbnail_url: string;
  author: string;
  published_at: string;
  tags?: string[];
}

function parseDateStr(dateStr: string): Date {
  return new Date(dateStr);
}

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const supabase = createClient();
    const fetchPosts = async () => {
      const { data } = await supabase
        .from('blog_posts')
        .select('id, slug, category, title, excerpt, thumbnail_url, author, published_at')
        .eq('is_published', true)
        .eq('publish_to_blog', true)
        .order('published_at', { ascending: false });
      if (data) setPosts(data);
      setLoading(false);
    };
    fetchPosts();
  }, []);

  const toggleCategory = (cat: string) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = selectedCategories.length > 0 || dateFrom !== '' || dateTo !== '';

  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      if (selectedCategories.length > 0) {
        const label = CATEGORY_LABELS[post.category] || post.category;
        if (!selectedCategories.includes(label)) return false;
      }
      const postDate = parseDateStr(post.published_at);
      if (dateFrom) {
        const from = new Date(dateFrom);
        if (postDate < from) return false;
      }
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        if (postDate > to) return false;
      }
      return true;
    });
  }, [posts, selectedCategories, dateFrom, dateTo]);

  const featured = filteredPosts[0];
  const rest = filteredPosts.slice(1);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav />
      <MarketTicker />
      <BreakingNewsBar />
      <main className="w-full max-w-screen-2xl mx-auto px-4 lg:px-6 xl:px-8 2xl:px-10 py-8">
        <div className="mb-6">
          <span className="text-primary text-xs font-bold uppercase tracking-widest">News & Insights</span>
          <h1 className="text-3xl font-black text-foreground mt-1">DMTV Blog</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Breaking crypto news, market analysis, and in-depth coverage of the digital asset ecosystem.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-xl p-4 mb-8">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5 text-muted-foreground text-xs font-semibold uppercase tracking-wider mr-1">
              <Filter size={13} />
              <span>Filter</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {FILTER_CATEGORIES.map((cat) => {
                const active = selectedCategories.includes(cat);
                return (
                  <button
                    key={cat}
                    onClick={() => toggleCategory(cat)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all duration-150 cursor-pointer ${
                      active
                        ? 'bg-primary border-primary text-white' :'bg-transparent border-border text-muted-foreground hover:border-primary hover:text-primary'
                    }`}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
            <div className="hidden sm:block w-px h-5 bg-border mx-1" />
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
                <Calendar size={12} />
                <span className="font-semibold uppercase tracking-wider">Date</span>
              </div>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-background border border-border rounded-md px-2 py-1 text-xs text-foreground focus:outline-none focus:border-primary cursor-pointer"
                title="From date"
              />
              <span className="text-muted-foreground text-xs">–</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-background border border-border rounded-md px-2 py-1 text-xs text-foreground focus:outline-none focus:border-primary cursor-pointer"
                title="To date"
              />
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors ml-auto cursor-pointer"
              >
                <X size={12} />
                Clear
              </button>
            )}
          </div>
          {hasActiveFilters && (
            <div className="mt-3 pt-3 border-t border-border flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">
                Showing <span className="text-foreground font-semibold">{filteredPosts.length}</span> article{filteredPosts.length !== 1 ? 's' : ''}
                {selectedCategories.length > 0 && (
                  <> in <span className="text-primary font-semibold">{selectedCategories.join(', ')}</span></>
                )}
              </span>
            </div>
          )}
        </div>

        {loading && (
          <div className="text-center py-20 text-muted-foreground text-sm">Loading articles…</div>
        )}

        {!loading && filteredPosts.length === 0 && (
          <div className="text-center py-20">
            <p className="text-muted-foreground text-lg font-semibold mb-2">No articles found</p>
            <p className="text-muted-foreground text-sm mb-4">Try adjusting your filters to see more results.</p>
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors cursor-pointer"
            >
              Clear Filters
            </button>
          </div>
        )}

        {featured && (
          <Link href={`/blog/${featured?.slug}`} className="block group mb-8">
            <div className="bg-card border border-border rounded-xl overflow-hidden card-hover">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative h-64 lg:h-auto min-h-[280px] bg-muted overflow-hidden">
                  <AppImage
                    src={featured?.thumbnail_url || '/assets/images/no_image.png'}
                    alt={featured?.title}
                    fill
                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                  />
                  <div className="absolute top-3 left-3">
                    <span className={`${CATEGORY_COLORS[featured?.category] || 'bg-primary'} text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider`}>
                      {CATEGORY_LABELS[featured?.category] || featured?.category}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <span className="bg-primary text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider">
                      Featured
                    </span>
                  </div>
                </div>
                <div className="p-6 lg:p-8 flex flex-col justify-center">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                    <span className="flex items-center gap-1"><Clock size={11} />5 min read</span>
                    <span>•</span>
                    <span>{new Date(featured?.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  <h2 className="text-xl lg:text-2xl font-black text-foreground leading-snug mb-3 group-hover:text-crypto-green transition-colors duration-150">
                    {featured?.title}
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-3">
                    {featured?.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
                        <User size={13} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground">{featured?.author}</p>
                      </div>
                    </div>
                    <span className="flex items-center gap-1.5 text-sm text-primary font-semibold group-hover:gap-2.5 transition-all duration-150">
                      Read More <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        )}

        {rest.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {rest?.map((post) => (
              <Link key={post?.id} href={`/blog/${post?.slug}`} className="block group">
                <article className="bg-card border border-border rounded-lg overflow-hidden card-hover h-full flex flex-col transition-all duration-300">
                  <div className="relative h-44 overflow-hidden bg-muted">
                    <AppImage
                      src={post?.thumbnail_url || '/assets/images/no_image.png'}
                      alt={post?.title}
                      fill
                      className="object-cover transition-transform duration-300 group-hover:scale-105"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                    <div className="absolute top-2 left-2">
                      <span className={`${CATEGORY_COLORS[post?.category] || 'bg-primary'} text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider`}>
                        {CATEGORY_LABELS[post?.category] || post?.category}
                      </span>
                    </div>
                    <div className="absolute top-2 right-2">
                      <span className="bg-black/70 text-gray-300 text-[10px] px-2 py-0.5 rounded">
                        {new Date(post?.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  </div>
                  <div className="p-3 flex flex-col gap-2 flex-1">
                    <h3 className="text-sm font-semibold text-foreground leading-snug group-hover:text-crypto-green transition-colors duration-150 line-clamp-2">
                      {post?.title}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                      {post?.excerpt}
                    </p>
                    <div className="mt-auto pt-1 flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{post?.author}</span>
                      <span className="flex items-center gap-1 text-xs text-primary font-medium group-hover:gap-2 transition-all duration-150">
                        Read More <ArrowRight size={11} />
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}
      </main>
      <FooterBar />
    </div>
  );
}
